/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import transloadit from 'js/lib/transloadit';
import util from 'js/lib/util';

import { Button } from '@coursera/cds-core';

import _t from 'i18n!nls/account-profile';

type Props = {
  label: string;
  onUpload: () => void;
  onProgress: (progress: number) => void;
  onSuccess: (imgs: any) => void;
  onError: (errorMessage: string) => void;
  onReset?: () => void;
  disabled?: boolean;
  render?: (onClick: () => void) => JSX.Element;
};

// TODO this component will be replaced by the new generic version as part of https://coursera.atlassian.net/browse/LPD-984
class PhotoUploadButton extends React.Component<Props> {
  inputRef: HTMLInputElement | null = null;

  formRef: HTMLSpanElement | null = null;

  // Use ref for transloadid instance id doesn't affect the view hence we don't need to keep it in state
  declare transloaditRef: React.MutableRefObject<any>;

  constructor(props: Props) {
    super(props);

    this.transloaditRef = React.createRef();
  }

  componentDidMount() {
    this.initForm();
  }

  componentWillUnmount() {
    this.resetUploader();
  }

  onUpload = () => {
    this.props.onUpload();
  };

  onProgress = (assembly: any) => {
    const progress = (assembly.bytes_received / assembly.bytes_expected) * 100;
    if (!isNaN(progress)) {
      this.props.onProgress(progress);
    }
  };

  onSuccess = (assembly: any) => {
    const imgs = {
      size120: util.makeHttps(assembly.results.resize_to_120[0].url),
      size60: util.makeHttps(assembly.results.resize_to_60[0].url),
      size24: util.makeHttps(assembly.results.resize_to_24[0].url),
      default: util.makeHttps(assembly.results[':original'][0].url),
    };
    this.props.onSuccess(imgs);
    this.removeForm();
  };

  onError = (error: { assembly: { message: string } }) => {
    this.props.onError(error?.assembly?.message || _t('an error has occurred'));
    this.removeForm();
  };

  initForm = () => {
    const uploader = transloadit(this.formRef, {
      params: {
        max_size: 1048576,
        auth: {
          key: '05912e90e83346abb96c261bf458b615',
        },
        template_id: '50f7efd339544e21958dbd415e8c650c',
      },
    });

    uploader.on('select', this.onUpload);
    uploader.on('progress', this.onProgress);
    uploader.on('success', this.onSuccess);
    uploader.on('error', this.onError);

    this.transloaditRef.current = uploader;
  };

  resetUploader = () => {
    const uploader = this.transloaditRef.current;
    uploader?.reset();
    uploader?.off('select', this.onUpload);
    uploader?.off('progress', this.onProgress);
    uploader?.off('success', this.onSuccess);
    uploader?.off('error', this.onError);
  };

  removeForm = () => {
    this.resetUploader();
    this.props.onReset?.();
  };

  onButtonClick = () => {
    this.inputRef?.click();
  };

  render() {
    const { label, render, disabled } = this.props;

    // transloadit will append an form element adjacent to the referenced element
    // So need the outer parent element to contain the appended form
    return (
      <div className="rc-PhotoUploadButton">
        {render ? (
          render(this.onButtonClick)
        ) : (
          <Button variant="primary" size="small" onClick={this.onButtonClick} disabled={disabled}>
            {label}
          </Button>
        )}

        <span
          className="transloadit-form"
          ref={(ref) => {
            this.formRef = ref;
          }}
        >
          <input
            id="photo-uploader"
            type="file"
            name="my_file"
            accept="image/*"
            ref={(ref) => {
              this.inputRef = ref;
            }}
          />
        </span>
      </div>
    );
  }
}

// we need to remount the component in order to pick up the new ref
const PhotoUploadButtonContainer = (props: Props) => {
  const [key, setKey] = useState(1);

  /**
   * Change a key to re-mount the component with transloadit transloadit requires re-initialization to function properly
   * @see https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html#recommendation-fully-uncontrolled-component-with-a-key
   */
  const handleReset = () => {
    setKey((prev) => prev + 1);
  };

  return (
    <div className="rc-PhotoUploadButtonContainer">
      <PhotoUploadButton key={key} {...props} onReset={handleReset} />
    </div>
  );
};

export default PhotoUploadButtonContainer;
